import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useContactListApi } from '@hero/api-hooks';
import { useFeature } from '@hero/web-context';
import type { Contact } from '@hero/schema';
import type { RedirectProp } from '@hero/web-hooks';
import { ContactIcon } from '@hero/web-icons';
import { ContactTable } from './ContactTable';
import { ButtonCreateEntity } from '../../../app/ButtonCreateEntity';
import { PageBack } from '../../../app/PageBack';
import { TableEmptyState } from '../../../app/Table';
import { ContactDrawer } from '../Drawer';
import { ClientDrawer } from '../../Client/Drawer';
import { SavedLocationDrawer } from '../../SavedLocation/Drawer';
import { OrderDrawer } from '../../Order/Drawer';
import { ContactEditModal } from '../Modal';

export interface ContactTableManagerProps {
  displayForUser?: 'customer' | 'carrier';
  /**
   * Mount the Contact / Client / SavedLocation / Order drawers from this manager.
   * Defaults to true so standalone usage keeps working. Pages that already mount
   * these drawers themselves (e.g. CRMPage) should pass `false` to avoid duplicate
   * drawer instances fighting for the same zustand store and URL params.
   */
  renderDrawers?: boolean;
  /**
   * Render a `<PageBack>` above the table. Hidden by default; pass
   * `true` to default to the More screen, or a typed route name /
   * config for a custom destination. See `RedirectProp` for the full
   * shape.
   */
  redirect?: RedirectProp;
}

export function ContactTableManager({
  displayForUser,
  renderDrawers = true,
  redirect,
}: ContactTableManagerProps) {
  const { t } = useTranslation(['m-contact']);
  const { isEnabled: canCreateContact } = useFeature('contact_module', 'sub_create_contact');

  const {
    data: contacts,
    isLoading,
    onLoadMore,
    hasNextPage,
    isFetchingNextPage,
    runQuery,
  } = useContactListApi({});

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [sortColumn, setSortColumn] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isCreateOpen, setIsCreateOpen] = useState(false);

  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      runQuery({ search: term || undefined });
    },
    [runQuery],
  );

  const handleStatusFilterChange = useCallback(
    (value: string | undefined) => {
      const next = value === statusFilter ? undefined : value;
      setStatusFilter(next);
      runQuery({ isOwnContact: next === 'own' ? true : next === 'other' ? false : undefined });
    },
    [runQuery, statusFilter],
  );

  const handleSort = useCallback(
    (column: string) => {
      const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
      setSortColumn(column);
    },
    [sortColumn, sortDirection],
  );

  const stats = useMemo(() => {
    const total = contacts.length;
    const own = contacts.filter((c: Contact) => c.isOwnContact).length;
    return { total, own, other: total - own };
  }, [contacts]);

  const statusFilterOptions = useMemo(
    () => [
      {
        value: 'own',
        label: t('m-contact:table.type_own', 'Own'),
        className: 'bg-primary-100 text-logoBlue',
      },
      {
        value: 'other',
        label: t('m-contact:table.type_other', 'Other'),
        className: 'bg-gray-100 text-gray-600',
      },
    ],
    [t],
  );

  const statusFilterCounts = useMemo(
    () => [
      { value: 'own', count: stats.own },
      { value: 'other', count: stats.other },
    ],
    [stats],
  );

  const emptyState = (
    <TableEmptyState
      icon={<ContactIcon className="h-7 w-7" />}
      title={t('m-contact:table.empty.title')}
      description={t('m-contact:table.empty.description')}
      action={
        canCreateContact ? (
          <ButtonCreateEntity
            label={t('m-contact:table.empty.cta')}
            onClick={() => setIsCreateOpen(true)}
          />
        ) : undefined
      }
    />
  );

  return (
    <>
      {redirect ? <PageBack redirect={redirect} /> : null}
      <ContactTable
        contacts={contacts}
        isLoading={isLoading}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        searchProps={{ term: searchTerm, setTerm: handleSearch }}
        onSort={handleSort}
        onLoadMore={onLoadMore}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        emptyState={emptyState}
        hasActiveFilters={Boolean(searchTerm) || Boolean(statusFilter)}>
        <ContactTable.Head>
          <ContactTable.Search />
          <div className="flex items-center gap-3">
            <ContactTable.Badge
              options={statusFilterOptions}
              counts={statusFilterCounts}
              value={statusFilter}
              onChange={handleStatusFilterChange}
              showCounts={false}
            />
            {canCreateContact && (
              <ButtonCreateEntity
                label={t('m-contact:modal.create_contact_title')}
                onClick={() => setIsCreateOpen(true)}
              />
            )}
          </div>
        </ContactTable.Head>
      </ContactTable>

      {renderDrawers && (
        <>
          <ContactDrawer syncUrl displayForUser={displayForUser || 'customer'} />
          <ClientDrawer displayForUser={displayForUser || 'customer'} />
          <SavedLocationDrawer displayForUser={displayForUser || 'customer'} />
          <OrderDrawer displayForUser={displayForUser || 'customer'} />
        </>
      )}
      <ContactEditModal isOpen={isCreateOpen} onClose={() => setIsCreateOpen(false)} />
    </>
  );
}
